import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, ExternalLink, Info, LogOut, User as UserIcon, ChevronDown, ChevronUp, X, ArrowLeft } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { getEmployeeTransitions, getRelatedBackground, getTransitionPersonDetail, getRelatedPersonDetail, getProfile } from '../api/insights';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';
import { trackCoreFeatureUsed } from '../analytics/mixpanel';
import { incrementInsightsProgress } from '../utils/insightsProgress';

const CONNECTION_FILTERS_STORAGE_KEY = 'insightsConnectionFiltersState';

/* ── helpers ── */
const avatarColor = (name = '') => {
  const cols = ['#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444', '#14B8A6'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return cols[Math.abs(h) % cols.length];
};

const getInitials = (name = '') => {
  const parts = String(name || '').trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const CompanyDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, openLogin, logout, loading: authLoading } = useAuth();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const resolvedParams = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    const parseIntOrNull = (value) => {
      if (value == null || value === '') return null;
      const n = Number(value);
      return Number.isNaN(n) ? null : n;
    };
    const parsedConnectionFilters = (() => {
      const raw = queryParams.get('cf');
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch {
        try {
          const decoded = decodeURIComponent(raw);
          const parsed = JSON.parse(decoded);
          return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {
          return null;
        }
      }
    })();
    const storedConnectionFilters = (() => {
      try {
        const raw = sessionStorage.getItem(CONNECTION_FILTERS_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const applied = parsed?.appliedConnectionFilters;
        return applied && typeof applied === 'object' ? applied : null;
      } catch {
        return null;
      }
    })();
    const parsedCrossIds = (() => {
      const raw = queryParams.get('crossIds');
      if (!raw) return [];
      try { const arr = JSON.parse(raw); return Array.isArray(arr) ? arr : []; } catch { return []; }
    })();
    const routeState = location.state || {};
    const rawInitialTab = routeState.initialPeopleTab ?? queryParams.get('initialPeopleTab') ?? 'transition';
    return {
      sourceOrgId: routeState.sourceOrgId ?? parseIntOrNull(queryParams.get('sourceOrgId')),
      sourceCompanyName: routeState.sourceCompanyName ?? queryParams.get('sourceCompanyName') ?? '',
      destOrgId: routeState.destOrgId ?? parseIntOrNull(queryParams.get('destOrgId')),
      companyName: routeState.companyName ?? queryParams.get('companyName') ?? '',
      initialHop: routeState.hop ?? parseIntOrNull(queryParams.get('hop')) ?? 1,
      startYear: routeState.startYear ?? queryParams.get('startYear') ?? '',
      endYear: routeState.endYear ?? queryParams.get('endYear') ?? '',
      role: routeState.role ?? queryParams.get('role') ?? '',
      location: routeState.location ?? queryParams.get('location') ?? '',
      connectionFilters: routeState.connectionFilters ?? parsedConnectionFilters ?? storedConnectionFilters,
      prefetchedRelatedBackground: routeState.relatedBackground,
      initialPeopleTab: rawInitialTab === 'related' ? 'related' : 'transition',
      crossMatchedEmployeeIds: Array.isArray(routeState.crossMatchedEmployeeIds) && routeState.crossMatchedEmployeeIds.length > 0
        ? routeState.crossMatchedEmployeeIds
        : parsedCrossIds,
    };
  }, [location.search, location.state]);

  const {
    sourceOrgId,
    sourceCompanyName,
    destOrgId,
    companyName,
    initialHop,
    startYear,
    endYear,
    role,
    location: initialLocation,
    connectionFilters,
    prefetchedRelatedBackground,
    initialPeopleTab,
    crossMatchedEmployeeIds,
  } = resolvedParams;

  const [hop, setHop] = useState(initialHop || 1);
  const [roleFilter, setRoleFilter] = useState(role || '');
  const [showOnlyMatched, setShowOnlyMatched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allHops, setAllHops] = useState({ 1: [], 2: [], 3: [] });
  const employees = useMemo(() => allHops[hop] || [], [allHops, hop]);
  const [selectedPerson, setSelectedPerson] = useState(null); // NEW: selected person for detail panel
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const detailCacheRef = useRef(new Map());
  const detailReqSeqRef = useRef(0);
  const [relatedBackground, setRelatedBackground] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [profileStatus, setProfileStatus] = useState('loading');
  const [peopleTab, setPeopleTab] = useState(initialPeopleTab || 'transition');
  const [hopNudge, setHopNudge] = useState('');
  const [showRelatedMatchCue, setShowRelatedMatchCue] = useState(false);

  const hasConnectionFilters = useMemo(() => {
    if (!connectionFilters || typeof connectionFilters !== 'object') return false;
    const keys = ['past_companies', 'past_roles', 'tenure_options', 'colleges', 'departments', 'batch_options'];
    return keys.some((k) => Array.isArray(connectionFilters[k]) && connectionFilters[k].length > 0);
  }, [connectionFilters]);

  const normalizedRoleFilter = useMemo(
    () => String(roleFilter || '').trim().toLowerCase(),
    [roleFilter]
  );

  const normalizedLocationFilter = useMemo(
    () => String(initialLocation || '').trim().toLowerCase(),
    [initialLocation]
  );

  const isRoleMatchedForPerson = useCallback((person) => {
    if (!normalizedRoleFilter) return false;
    if (person?.role_match) return true;
    return (person?.experience_history || []).some((exp) =>
      String(exp?.role || '').toLowerCase().includes(normalizedRoleFilter)
    );
  }, [normalizedRoleFilter]);

  const isLocationMatchedForPerson = useCallback((person) => {
    if (!normalizedLocationFilter) return false;

    // If backend already flagged it, prefer that (for performance and consistency)
    if (person?.location_match) return true;

    const history = person?.experience_history || [];
    return history.some((exp) => {
      const seg = exp.transition_segment || '';

      // If it's a transition person, check segments >= current hop
      if (seg.startsWith('hop_')) {
        const hNum = parseInt(seg.split('_')[1]);
        if (hNum === hop) {
          return String(exp?.location || exp?.address || '').toLowerCase().includes(normalizedLocationFilter);
        }
        return false;
      }

      // For related profile or if segment is missing (backend fallback), 
      // check if any current experience at the destination matches
      const atDest = String(exp?.organization || '').toLowerCase() === String(companyName || '').toLowerCase();
      const isCurrent = !exp.end_date;
      if (atDest && isCurrent) {
        return String(exp?.location || exp?.address || '').toLowerCase().includes(normalizedLocationFilter);
      }

      return false;
    });
  }, [normalizedLocationFilter, hop, companyName]);

  const crossMatchedIdSet = useMemo(() => {
    if (!Array.isArray(crossMatchedEmployeeIds) || crossMatchedEmployeeIds.length === 0) return new Set();
    return new Set(crossMatchedEmployeeIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n)));
  }, [crossMatchedEmployeeIds]);
  const hasCrossMatchesContext = crossMatchedIdSet.size > 0;
  const hasLocationFilter = Boolean(normalizedLocationFilter);
  const hasMatchesToggle = hasConnectionFilters || hasCrossMatchesContext || hasLocationFilter;

  const filteredEmployees = useMemo(() => {
    let list = Array.isArray(employees) ? employees : [];
    const hasRoleFilter = normalizedRoleFilter.length > 0;

    if (showOnlyMatched && (hasMatchesToggle || hasRoleFilter)) {
      list = list.filter((emp) => (
        crossMatchedIdSet.has(Number(emp?.employee_id))
        || Boolean(emp?.is_match)
        || (hasRoleFilter && isRoleMatchedForPerson(emp))
        || isLocationMatchedForPerson(emp)
      ));
    }

    list = [...list].sort((a, b) => {
      const aCross = crossMatchedIdSet.has(Number(a?.employee_id));
      const bCross = crossMatchedIdSet.has(Number(b?.employee_id));
      if (aCross !== bCross) return aCross ? -1 : 1;

      const aMatch = Boolean(a?.is_match);
      const bMatch = Boolean(b?.is_match);
      if (aMatch !== bMatch) return aMatch ? -1 : 1;

      const aLoc = isLocationMatchedForPerson(a);
      const bLoc = isLocationMatchedForPerson(b);
      if (aLoc !== bLoc) return aLoc ? -1 : 1;

      const aRole = hasRoleFilter && isRoleMatchedForPerson(a);
      const bRole = hasRoleFilter && isRoleMatchedForPerson(b);
      if (aRole !== bRole) return aRole ? -1 : 1;

      return 0;
    });
    return list;
  }, [employees, normalizedRoleFilter, showOnlyMatched, hasMatchesToggle, isRoleMatchedForPerson, isLocationMatchedForPerson, crossMatchedIdSet]);

  const transitionEmptyMessage = useMemo(() => {
    const hasPeopleInHop = Array.isArray(employees) && employees.length > 0;
    const hasRoleFilter = Boolean(String(roleFilter || '').trim());
    if (hasPeopleInHop && hasRoleFilter) return 'No people in this hop match this role filter.';
    if (hasPeopleInHop && showOnlyMatched && hasConnectionFilters)
      return 'No people in this hop match your advanced filters. Uncheck "Show only matches" to see all.';
    return 'No transition people found for this hop.';
  }, [employees, roleFilter, showOnlyMatched, hasConnectionFilters]);

  const filteredRelatedBackground = useMemo(() => {
    let list = Array.isArray(relatedBackground) ? relatedBackground : [];
    const hasRoleFilter = normalizedRoleFilter.length > 0;

    if (showOnlyMatched && (hasMatchesToggle || hasRoleFilter)) {
      list = list.filter((p) => (
        crossMatchedIdSet.has(Number(p?.employee_id))
        || Boolean(p?.is_match)
        || (hasRoleFilter && isRoleMatchedForPerson(p))
        || isLocationMatchedForPerson(p)
      ));
    }

    list = [...list].sort((a, b) => {
      const aCross = crossMatchedIdSet.has(Number(a?.employee_id));
      const bCross = crossMatchedIdSet.has(Number(b?.employee_id));
      if (aCross !== bCross) return aCross ? -1 : 1;

      const aMatch = Boolean(a?.is_match);
      const bMatch = Boolean(b?.is_match);
      if (aMatch !== bMatch) return aMatch ? -1 : 1;

      const aLoc = isLocationMatchedForPerson(a);
      const bLoc = isLocationMatchedForPerson(b);
      if (aLoc !== bLoc) return aLoc ? -1 : 1;

      const aRole = hasRoleFilter && isRoleMatchedForPerson(a);
      const bRole = hasRoleFilter && isRoleMatchedForPerson(b);
      if (aRole !== bRole) return aRole ? -1 : 1;

      return 0;
    });
    return list;
  }, [relatedBackground, normalizedRoleFilter, showOnlyMatched, hasMatchesToggle, isRoleMatchedForPerson, isLocationMatchedForPerson, crossMatchedIdSet]);

  useEffect(() => {
    if (!hasMatchesToggle) {
      setShowRelatedMatchCue(false);
      setShowOnlyMatched(false);
      return;
    }
    setShowOnlyMatched(true);
    if (peopleTab === 'related') {
      setShowRelatedMatchCue(false);
      return;
    }
    const hasMatchedRelated = (relatedBackground || []).some((p) => (
      p?.is_match ||
      isRoleMatchedForPerson(p) ||
      isLocationMatchedForPerson(p) ||
      crossMatchedIdSet.has(Number(p?.employee_id))
    ));
    setShowRelatedMatchCue(hasMatchedRelated);
  }, [hasMatchesToggle, peopleTab, relatedBackground, isRoleMatchedForPerson, isLocationMatchedForPerson, crossMatchedIdSet]);

  useEffect(() => {
    if (crossMatchedIdSet.size === 0) return;
    if (peopleTab !== 'transition') return;
    const anyTransitionCross = (filteredEmployees || []).some((p) => crossMatchedIdSet.has(Number(p?.employee_id)));
    const anyRelatedCross = (filteredRelatedBackground || []).some((p) => crossMatchedIdSet.has(Number(p?.employee_id)));
    setShowRelatedMatchCue(!anyTransitionCross && anyRelatedCross);
  }, [crossMatchedIdSet, peopleTab, filteredEmployees, filteredRelatedBackground]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { openLogin(); return; }
    if (!sourceOrgId || !destOrgId) {
      setError('Missing source or destination organization information.');
      setLoading(false);
      return;
    }
    const startDate = startYear ? `${startYear}-01-01` : null;
    const endDate = endYear ? `${endYear}-12-31` : null;

    setLoading(true);
    setError(null);
    setSelectedPerson(null);

    const fetchHop = (h) => getEmployeeTransitions(sourceOrgId, destOrgId, {
      hop: h, startDate, endDate,
      role: roleFilter || undefined,
      location: initialLocation || undefined,
      connectionFilters: hasConnectionFilters ? connectionFilters : undefined,
    }).then(data => {
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => {
        if ((a.is_match || false) !== (b.is_match || false)) return a.is_match ? -1 : 1;
        if ((a.role_match || false) !== (b.role_match || false)) return a.role_match ? -1 : 1;
        return String(a.employee_name || '').localeCompare(String(b.employee_name || ''));
      });
      return { hop: h, list };
    });

    Promise.all([fetchHop(1), fetchHop(2), fetchHop(3)])
      .then((results) => {
        const newData = {};
        let totalCount = 0;
        results.forEach(res => {
          newData[res.hop] = res.list;
          totalCount += res.list.length;
        });
        setAllHops(newData);

        trackCoreFeatureUsed('transition_analysis', {
          source_org_id: sourceOrgId, dest_org_id: destOrgId,
          company_name: companyName, total_employee_count: totalCount,
        });
      })
      .catch((err) => {
        setError(err.message || 'Failed to load employee transitions');
        setAllHops({ 1: [], 2: [], 3: [] });
      })
      .finally(() => setLoading(false));
  }, [sourceOrgId, destOrgId, startYear, endYear, roleFilter, initialLocation, connectionFilters, hasConnectionFilters, user, openLogin, authLoading, companyName]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    setProfileStatus('loading');
    getProfile()
      .then((data) => {
        const profile = (data?.profile && typeof data.profile === 'object') ? data.profile : (data && typeof data === 'object' ? data : {});
        const workItems = Array.isArray(profile.work_experiences) ? profile.work_experiences : [];
        const eduItems = Array.isArray(profile.educations) ? profile.educations : [];
        const hasWork = workItems.length > 0 && workItems.some((item) => String(item?.company || item?.organization || '').trim() || String(item?.role || '').trim());
        const hasEducation = eduItems.length > 0 && eduItems.some((item) => String(item?.school || item?.institution || '').trim() || String(item?.degree || item?.field_of_study || '').trim());
        setProfileStatus(hasWork || hasEducation ? 'ready' : 'missing');
      })
      .catch(() => setProfileStatus('error'));
  }, [authLoading, user]);

  useEffect(() => {
    if (!sourceOrgId || !destOrgId || !user) return;
    if (profileStatus !== 'ready') { setRelatedBackground([]); setLoadingRelated(false); return; }
    const sortRelated = (list) => {
      list.sort((a, b) => {
        if ((a.is_match || false) !== (b.is_match || false)) return a.is_match ? -1 : 1;
        return String(a.employee_name || '').localeCompare(String(b.employee_name || ''));
      });
      return list;
    };
    const prefetched = prefetchedRelatedBackground;
    if (prefetched && typeof prefetched === 'object' && Array.isArray(prefetched.related)) {
      setRelatedBackground(sortRelated([...prefetched.related]));
      setLoadingRelated(false);
      return;
    }
    const startDate = startYear ? `${startYear}-01-01` : null;
    const endDate = endYear ? `${endYear}-12-31` : null;
    setLoadingRelated(true);
    getRelatedBackground(sourceOrgId, destOrgId, {
      startDate, endDate,
      location: initialLocation || undefined,
      connectionFilters: hasConnectionFilters ? connectionFilters : undefined,
    })
      .then((data) => { const list = Array.isArray(data.related) ? data.related : []; setRelatedBackground(sortRelated(list)); })
      .catch(() => setRelatedBackground([]))
      .finally(() => setLoadingRelated(false));
  }, [sourceOrgId, destOrgId, startYear, endYear, initialLocation, user, connectionFilters, hasConnectionFilters, profileStatus, prefetchedRelatedBackground]);

  const totalEmployees = peopleTab === 'related' ? filteredRelatedBackground.length : filteredEmployees.length;

  const getMatchDetails = (person) => {
    const details = person?.filter_match_details;
    if (!details || typeof details !== 'object') return { work_matches: [], education_matches: [] };
    return {
      work_matches: Array.isArray(details.work_matches) ? details.work_matches : [],
      education_matches: Array.isArray(details.education_matches) ? details.education_matches : [],
    };
  };

  const buildWorkKey = (item) => [item?.organization || '', item?.role || '', item?.start_date || '', item?.end_date || ''].join('|');

  const experienceMatchedByFilter = (person, exp) => {
    const details = getMatchDetails(person);
    const workKeys = new Set((details.work_matches || []).map(buildWorkKey));
    const matchedByKey = workKeys.has(buildWorkKey(exp));

    let matchedByLocation = false;
    if (normalizedLocationFilter) {
      const isMatchText = String(exp?.location || exp?.address || '').toLowerCase().includes(normalizedLocationFilter);
      if (isMatchText) {
        const seg = exp.transition_segment || '';
        if (seg.startsWith('hop_')) {
          const hNum = parseInt(seg.split('_')[1]);
          if (hNum === hop) matchedByLocation = true;
        } else if (!seg) {
          const atDest = String(exp?.organization || '').toLowerCase() === String(companyName || '').toLowerCase();
          const isCurrent = !exp.end_date;
          if (atDest && isCurrent) matchedByLocation = true;
        }
      }
    }

    return matchedByKey || matchedByLocation;
  };

  const personHasFilterEvidence = (person) => {
    const details = getMatchDetails(person);
    return (details.work_matches || []).length > 0 || (details.education_matches || []).length > 0;
  };

  const getTopCompanyNames = (experienceHistory = []) => {
    const names = [];
    const seen = new Set();
    for (const exp of experienceHistory || []) {
      const org = String(exp?.organization || '').trim();
      const key = org.toLowerCase();
      if (!org || seen.has(key)) continue;
      seen.add(key);
      names.push(org);
      if (names.length >= 2) break;
    }
    return names;
  };

  const buildLinkedInSearchUrl = (name, companyNames = []) => {
    const queryParts = [String(name || '').trim(), ...companyNames.slice(0, 2).map((c) => String(c || '').trim()).filter(Boolean), 'linkedin'].filter(Boolean);
    return `https://www.google.com/search?q=${encodeURIComponent(queryParts.join(' '))}`;
  };

  const getCurrentRole = (person) => {
    const history = person?.experience_history || [];
    if (!history.length) return '';
    // Find the most recent role at destOrgId / companyName
    const atDest = history.find(e => String(e?.organization || '').toLowerCase() === String(companyName || '').toLowerCase());
    if (atDest?.role) return atDest.role;
    // fallback: first entry
    return history[0]?.role || '';
  };

  const handleHopChange = (value) => {
    if (peopleTab === 'related') { setHopNudge('Hop works for Transition People. Switch to that tab to change it.'); return; }
    setHopNudge('');
    setHop(value);
  };

  const handlePeopleTabChange = (tab) => {
    if (tab === 'related') setShowRelatedMatchCue(false);
    setPeopleTab(tab);
    setSelectedPerson(null);
  };

  const { isDark } = useTheme();
  const appCls = isDark ? 'dark-app' : 'light-app';
  const tabCls = isDark ? 'dark-tab' : 'light-tab';
  const bgPage = isDark ? 'rgba(13,16,25,0.92)' : 'rgba(238,242,251,0.95)';
  const borderSub = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(59,130,246,0.12)';
  const txtBody = isDark ? 'rgba(255,255,255,0.82)' : '#1e293b';
  const txtMuted = isDark ? 'rgba(255,255,255,0.5)' : '#64748b';
  const accentColor = isDark ? '#F59E0B' : '#2563eb';
  const accentBg = isDark ? 'linear-gradient(135deg,#F59E0B,#D97706)' : 'linear-gradient(135deg,#3b82f6,#2563eb)';
  const accentTxt = isDark ? '#1C1814' : '#ffffff';
  const inputBg = isDark ? '#0d1019' : '#ffffff';
  const inputBorder = isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1';
  const inputTxt = isDark ? 'rgba(255,255,255,0.9)' : '#1e293b';
  const panelBg = isDark ? 'rgba(13,16,25,0.97)' : '#f8faff';
  const panelBorder = isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0';

  if (authLoading) {
    return (
      <div className={`${appCls} compact-ui flex items-center justify-center`} style={{ minHeight: '100vh' }}>
        <Loader2 className="w-10 h-10 animate-spin" style={{ color: accentColor }} />
      </div>
    );
  }

  const currentList = peopleTab === 'transition' ? filteredEmployees : filteredRelatedBackground;

  const loadPersonDetail = (person, type) => {
    if (!person?.employee_id) return;
    const cacheKey = type === 'transition'
      ? `${type}:${hop}:${person.employee_id}`
      : `${type}:${person.employee_id}`;
    const cached = detailCacheRef.current.get(cacheKey);
    if (cached) {
      setSelectedPerson((prev) => (prev && prev.employee_id === person.employee_id ? { ...prev, ...cached } : prev));
      return;
    }

    const reqSeq = ++detailReqSeqRef.current;
    setDetailLoadingId(person.employee_id);
    setDetailError(null);
    const startDate = startYear ? `${startYear}-01-01` : null;
    const endDate = endYear ? `${endYear}-12-31` : null;

    const fetcher = type === 'transition'
      ? getTransitionPersonDetail(person.employee_id, {
        sourceOrgId,
        destOrgId,
        hop,
        startDate,
        endDate,
        connectionFilters: hasConnectionFilters ? connectionFilters : undefined,
      })
      : getRelatedPersonDetail(person.employee_id, {
        destOrgId,
        connectionFilters: hasConnectionFilters ? connectionFilters : undefined,
      });

    fetcher
      .then((data) => {
        if (reqSeq !== detailReqSeqRef.current) return;
        detailCacheRef.current.set(cacheKey, data);
        setSelectedPerson((prev) => (prev && prev.employee_id === person.employee_id ? { ...prev, ...data } : prev));
      })
      .catch((err) => {
        if (reqSeq !== detailReqSeqRef.current) return;
        setDetailError(err.message || 'Failed to load details');
      })
      .finally(() => {
        if (reqSeq !== detailReqSeqRef.current) return;
        setDetailLoadingId(null);
      });
  };

  const selectPerson = (person, isTransition) => {
    const isSame = selectedPerson?.employee_id === person.employee_id;
    if (isSame) {
      setSelectedPerson(null);
      setDetailError(null);
      return;
    }
    incrementInsightsProgress(user?.id || user?.email, sourceOrgId, 'referrals');
    setSelectedPerson(person);
    const type = isTransition ? 'transition' : 'related';
    loadPersonDetail(person, type);
  };

  /* ── Person card (compact left-panel row) ── */
  const PersonRow = ({ person, isTransition }) => {
    const isSelected = selectedPerson?.employee_id === person.employee_id;
    const roleMatched = isRoleMatchedForPerson(person);
    const locMatched = isLocationMatchedForPerson(person);
    const isCrossMatched = crossMatchedIdSet.has(Number(person?.employee_id));
    const isHighlighted = person.is_match || roleMatched || locMatched || isCrossMatched;
    const color = avatarColor(person.employee_name || '');
    const initials = getInitials(person.employee_name || '');
    const currentRole = getCurrentRole(person);

    const tags = [];
    if (isTransition) {
      if (isCrossMatched) tags.push({ label: 'Matched', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' });
      if (person.is_match) tags.push({ label: 'Filter Matched', color: '#60A5FA', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.3)' });
      if (locMatched) tags.push({ label: 'Location Matched', color: '#34D399', bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.3)' });
      if (roleMatched) tags.push({ label: 'Role Matched', color: '#60A5FA', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.3)' });
    } else {
      const connType = person.connection_type;
      if (connType === 'past_company_and_college') {
        tags.push({ label: 'Company & College', color: '#7DD3FC', bg: 'rgba(14,165,233,0.12)', border: 'rgba(14,165,233,0.25)' });
      } else if (connType === 'past_company') {
        tags.push({ label: 'Past Company', color: '#7DD3FC', bg: 'rgba(14,165,233,0.12)', border: 'rgba(14,165,233,0.25)' });
      } else if (connType === 'college') {
        tags.push({ label: 'College', color: '#7DD3FC', bg: 'rgba(14,165,233,0.12)', border: 'rgba(14,165,233,0.25)' });
      }
      if (isCrossMatched) tags.push({ label: 'Matched', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' });
      if (person.is_match) tags.push({ label: 'Filter Matched', color: '#60A5FA', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.3)' });
      if (locMatched) tags.push({ label: 'Location Matched', color: '#34D399', bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.3)' });
    }

    return (
      <div>
        <div
          className="cd-person-row"
          onClick={() => selectPerson(person, isTransition)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            cursor: 'pointer',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
            background: isSelected
              ? 'rgba(245,158,11,0.08)'
              : isHighlighted
                ? 'rgba(59,130,246,0.12)'
                : 'transparent',
            borderLeft: isSelected
              ? '3px solid #F59E0B'
              : isHighlighted
                ? '3px solid rgba(59,130,246,0.9)'
                : '3px solid transparent',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          onMouseLeave={e => {
            if (!isSelected) e.currentTarget.style.background = isHighlighted ? 'rgba(59,130,246,0.12)' : 'transparent';
          }}
        >
          {/* Avatar */}
          <div style={{
            width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
            background: `${color}20`, border: `1.5px solid ${color}50`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 800, color,
          }}>
            {initials}
          </div>

          {/* Name + meta */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.9)', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {person.employee_name || `Employee ${person.employee_id}`}
            </p>
            {currentRole && (
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 4 }}>
                {currentRole}
              </p>
            )}
            {/* Tags */}
            {tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {tags.map((tag, i) => (
                  <span key={i} style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                    padding: '2px 7px', borderRadius: 99,
                    background: tag.bg, color: tag.color, border: `1px solid ${tag.border}`,
                  }}>
                    {tag.label}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="cd-row-chevron" style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>
            {isSelected ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </div>

        {isSelected && (
          <div className="cd-mobile-detail">
            <DetailPanel
              person={person}
              isTransition={isTransition}
              compact
              detailLoading={detailLoadingId === person.employee_id}
              detailError={detailError}
            />
          </div>
        )}
      </div>
    );
  };

  /* ── Detail panel for selected person ── */
  const DetailPanel = ({ person, isTransition, compact = false, detailLoading = false, detailError: detailErrorMsg = null }) => {
    if (!person) return (
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        color: 'rgba(255,255,255,0.2)', fontSize: 13, gap: 8, padding: 40,
      }}>
        <UserIcon size={32} style={{ opacity: 0.2 }} />
        <p>Select a person to see their details</p>
      </div>
    );

    const color = avatarColor(person.employee_name || '');
    const initials = getInitials(person.employee_name || '');
    const roleMatched = isTransition ? isRoleMatchedForPerson(person) : false;
    const locMatched = isLocationMatchedForPerson(person);
    const linkedInUrl = person.linkedin_url || buildLinkedInSearchUrl(person.employee_name || `Employee ${person.employee_id}`, getTopCompanyNames(person.experience_history));

    return (
      <div style={{ flex: 1, overflow: compact ? 'visible' : 'auto', padding: compact ? '12px 16px 20px' : '12px 24px 20px' }}>
        {/* Filter match evidence */}
        {(hasConnectionFilters && person.is_match && personHasFilterEvidence(person)) && (
          <div style={{ marginBottom: 20, padding: '14px 16px', borderRadius: 12, background: 'rgba(96,165,250,0.07)', border: '1px solid rgba(96,165,250,0.2)' }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: '#60A5FA', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>Filter match evidence</p>
            {(getMatchDetails(person).work_matches || []).length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Work</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(getMatchDetails(person).work_matches || []).map((m, idx) => (
                    <span key={`work-${idx}`} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 8, background: 'rgba(96,165,250,0.1)', color: '#93C5FD', border: '1px solid rgba(96,165,250,0.2)' }}>
                      {m.organization || 'Unknown'}{m.role ? ` · ${m.role}` : ''}{Array.isArray(m.matched_fields) && m.matched_fields.length > 0 ? ` (${m.matched_fields.join(', ')})` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {(getMatchDetails(person).education_matches || []).length > 0 && (
              <div>
                <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Education</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(getMatchDetails(person).education_matches || []).map((m, idx) => (
                    <span key={`edu-${idx}`} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 8, background: 'rgba(96,165,250,0.1)', color: '#93C5FD', border: '1px solid rgba(96,165,250,0.2)' }}>
                      {m.school || 'Unknown'}{m.degree ? ` · ${m.degree}` : ''}{Array.isArray(m.matched_fields) && m.matched_fields.length > 0 ? ` (${m.matched_fields.join(', ')})` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Experience history */}
        {detailLoading && (!(person.experience_history || []).length) && (
          <div style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.5)' }}>
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: accentColor }} />
            <span style={{ fontSize: 12 }}>Loading career timeline…</span>
          </div>
        )}
        {detailErrorMsg && !detailLoading && (
          <div style={{ padding: '8px 0', color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
            {detailErrorMsg}
          </div>
        )}
        {(person.experience_history || []).length > 0 && (
          <div>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14 }}>
              Career Timeline of {person.employee_name || 'Employee'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {(person.experience_history || []).map((exp, idx) => {
                const seg = exp.transition_segment || 'prior';
                const isHopUsed = seg === `hop_${hop}`;
                const filterMatchedRow = isTransition && experienceMatchedByFilter(person, exp);

                let dotColor = 'rgba(255,255,255,0.2)';
                if (isTransition) {
                  if (seg === 'source') dotColor = '#F59E0B';
                  else if (isHopUsed) dotColor = '#34D399';
                  else if (seg.startsWith('hop_')) dotColor = '#60A5FA';
                }
                if (filterMatchedRow) dotColor = '#3B82F6';

                return (
                  <div key={idx} style={{ display: 'flex', gap: 14, position: 'relative' }}>
                    {/* Timeline line */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 14, flexShrink: 0 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: dotColor, flexShrink: 0, marginTop: 4, border: `2px solid ${dotColor === 'rgba(255,255,255,0.2)' ? 'rgba(255,255,255,0.1)' : dotColor}`, boxShadow: dotColor !== 'rgba(255,255,255,0.2)' ? `0 0 6px ${dotColor}60` : 'none' }} />
                      {idx < (person.experience_history || []).length - 1 && (
                        <div style={{ width: 1, flex: 1, background: 'rgba(255,255,255,0.08)', minHeight: 20 }} />
                      )}
                    </div>
                    {/* Content */}
                    <div style={{
                      flex: 1, paddingBottom: idx < (person.experience_history || []).length - 1 ? 18 : 0,
                      background: filterMatchedRow ? 'rgba(59,130,246,0.07)' : 'transparent',
                      borderRadius: filterMatchedRow ? 8 : 0,
                      padding: filterMatchedRow ? '6px 10px 10px' : '0 0 18px',
                    }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: dotColor !== 'rgba(255,255,255,0.2)' ? '#fff' : 'rgba(255,255,255,0.75)', marginBottom: 2 }}>
                        {exp.organization || 'Unknown'}
                      </p>
                      {exp.role && (
                        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 2 }}>{exp.role}</p>
                      )}
                      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
                        {[exp.start_date, exp.end_date].filter(Boolean).join(' – ') || '—'}
                        {(exp.location || exp.address) && ` · ${exp.location || exp.address}`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* LinkedIn Button at the bottom */}
        <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <a
            href={linkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '12px 20px', borderRadius: 12, fontSize: 14, fontWeight: 700,
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(255,255,255,0.85)', textDecoration: 'none', transition: 'all 0.2s',
            }}
          >
            <ExternalLink size={16} /> View LinkedIn Profile
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className={`${appCls} compact-ui`} style={{ minHeight: '100vh', paddingTop: 56 }}>
      {/* Header */}
      <header className="z-50" style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 56, background: bgPage, borderBottom: `1px solid ${borderSub}`, backdropFilter: 'blur(12px)' }}>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 flex items-center justify-between gap-4" style={{ height: 56 }}>
          <div className="flex items-center gap-4 sm:gap-6 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all text-[rgba(255,255,255,0.6)] hover:text-white hover:bg-white/5"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <div className="h-6 w-px bg-white/10" />
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-8 h-8 flex items-center justify-center bg-white rounded-md p-1">
                <img src="/assets/logo.png" alt="fairhire logo" className="w-full h-full object-contain" />
              </div>
              <span className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(255,255,255,0.9)' }}>fairhire</span>
            </div>
            <div className="hidden sm:block h-8 w-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
            <div>
              <h1 className="text-base sm:text-xl font-bold" style={{ fontFamily: "'Inter', serif", color: 'rgba(255,255,255,0.9)' }}>
                {companyName || 'Company'} – Employees
              </h1>
              <p className="text-xs sm:text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
                {startYear && endYear
                  ? `Alumni who reached ${companyName || 'this company'} in hop ${hop} (${startYear}–${endYear})`
                  : `Alumni who reached ${companyName || 'this company'} in hop ${hop}`}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between lg:justify-end gap-4 sm:gap-6 w-full lg:w-auto">
            <div className="text-left lg:text-right">
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>Total People</p>
              <p className="text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: '#F59E0B' }}>{totalEmployees}</p>
            </div>
            <div className="relative">
              {user ? (
                <div className="flex items-center gap-3 sm:gap-4">
                  <button onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} className="w-10 h-10 rounded-full flex items-center justify-center transition-all" style={{ background: 'rgba(255,255,255,0.07)' }}>
                    <UserIcon className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.7)' }} />
                  </button>
                  <AnimatePresence>
                    {isProfileMenuOpen && (
                      <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute right-0 top-full mt-2 w-52 rounded-xl shadow-xl p-2 z-40" style={{ background: '#0d1019', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div className="px-3 py-2 mb-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Signed in as</p>
                          <p className="text-sm font-semibold truncate" style={{ color: 'rgba(255,255,255,0.85)' }}>{user.name}</p>
                        </div>
                        <button onClick={() => { setIsProfileMenuOpen(false); navigate('/profile'); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all" style={{ color: 'rgba(255,255,255,0.7)' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <UserIcon className="w-4 h-4" /> Profile
                        </button>
                        <button onClick={() => { logout(); setIsProfileMenuOpen(false); navigate('/'); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all" style={{ color: '#F87171' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <LogOut className="w-4 h-4" /> Logout
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <button onClick={() => openLogin()} className="px-4 sm:px-6 py-2 rounded-full text-sm font-semibold transition-all" style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)' }}>Login</button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Controls bar */}
      <div style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-4">
          {/* Hop selector */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label className="flex items-center gap-1.5 text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.4)', paddingLeft: 4 }}>
              Transitions
            </label>
            <div style={{ display: 'flex', gap: 6, background: 'rgba(255,255,255,0.03)', padding: 4, borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
              {[
                { val: 1, label: '1st transition' },
                { val: 2, label: '2nd transition' },
                { val: 3, label: '3rd transition' }
              ].map(t => {
                const isActive = hop === t.val;
                const peopleInHop = allHops[t.val] || [];
                const hasPeople = peopleInHop.length > 0;
                const hasMatch = peopleInHop.some(p =>
                  p.is_match ||
                  crossMatchedIdSet.has(Number(p.employee_id)) ||
                  (normalizedRoleFilter.length > 0 && isRoleMatchedForPerson(p)) ||
                  isLocationMatchedForPerson(p)
                );

                return (
                  <button
                    key={t.val}
                    onClick={() => handleHopChange(t.val)}
                    style={{
                      padding: '8px 14px', borderRadius: 9,
                      fontSize: 12, fontWeight: isActive ? 800 : 600,
                      background: isActive ? '#F59E0B' : 'transparent',
                      color: isActive ? '#1C1917' : (hasMatch ? '#F59E0B' : (hasPeople ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.25)')),
                      border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                      display: 'flex', alignItems: 'center', gap: 6,
                      position: 'relative'
                    }}
                  >
                    <span>{t.label}</span>
                    {hasMatch && !isActive && (
                      <span style={{
                        width: 6, height: 6, borderRadius: '50%', background: '#F59E0B',
                        boxShadow: '0 0 10px #F59E0B'
                      }} />
                    )}
                    {hasPeople && !isActive && (
                      <span style={{ fontSize: 10, opacity: 0.4 }}>{peopleInHop.length}</span>
                    )}
                  </button>
                );
              })}
            </div>
            {hopNudge && <p className="text-[11px] text-amber-500 mt-1.5 px-1">{hopNudge}</p>}
          </div>

          {/* Role filter */}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Filter by role (optional)</label>
            <input type="text" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} placeholder="e.g. Product Manager" className="w-full sm:w-64 px-3 py-2 rounded-lg text-sm focus:outline-none" />
          </div>

          {/* Show only matches toggle */}
          {hasMatchesToggle && (
            <div className="flex items-center gap-2 mt-5 ml-auto">
              <label className="relative inline-flex items-center cursor-pointer group">
                <input type="checkbox" className="sr-only peer" checked={showOnlyMatched} onChange={() => setShowOnlyMatched(!showOnlyMatched)} />
                <div className="w-9 h-5 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white/40 after:border-white/10 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600/60 peer-checked:after:bg-white"></div>
                <span className="ml-3 text-sm font-medium transition-colors" style={{ color: showOnlyMatched ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)' }}>Show only matches</span>
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 text-[10px] font-bold uppercase tracking-wider border border-blue-500/20">Filters Active</span>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 flex">
          <button type="button" onClick={() => handlePeopleTabChange('transition')} className={`${tabCls} px-4 py-3 text-sm font-medium ${peopleTab === 'transition' ? 'active' : ''}`}>
            Transition People
          </button>
          <button type="button" onClick={() => handlePeopleTabChange('related')} className={`${tabCls} px-4 py-3 text-sm font-medium inline-flex items-center gap-2 ${peopleTab === 'related' ? 'active' : ''}`}>
            Related to Your Background
            {(loadingRelated || profileStatus === 'loading') && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {profileStatus === 'missing' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-[10px] font-semibold uppercase tracking-wide border border-amber-500/20">Missing Profile</span>
            )}
            {profileStatus === 'ready' && !loadingRelated && relatedBackground.length === 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-[rgba(255,255,255,0.4)] text-[10px] font-semibold uppercase tracking-wide border border-white/10">No People Found</span>
            )}
            {showRelatedMatchCue && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#22C55E]/15 text-[#22C55E] text-[10px] font-semibold uppercase tracking-wide border border-[#22C55E]/20">New match</span>
            )}
          </button>
        </div>
      </div>

      {/* Main two-panel content */}
      {error ? (
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 pt-6">
          <div className="rounded-lg px-6 py-4" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#FCA5A5' }}>{error}</div>
        </div>
      ) : (
        <div className="cd-two-panel">
          {/* Left: person list */}
          <div className="cd-left-panel">
            {/* Sorted by label */}
            <div style={{ padding: '10px 16px 6px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <p style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                SORTED BY LIKELINESS TO MATCH
              </p>
            </div>

            {peopleTab === 'transition' && (
              <>
                {loading ? (
                  <div className="flex items-center justify-center py-24">
                    <Loader2 className="w-8 h-8 animate-spin" style={{ color: accentColor }} />
                  </div>
                ) : filteredEmployees.length === 0 ? (
                  <div style={{ padding: 20, fontSize: 13, color: 'rgba(255,255,255,0.35)', lineHeight: 1.6 }}>{transitionEmptyMessage}</div>
                ) : (
                  filteredEmployees.map((emp) => (
                    <PersonRow key={emp.employee_id} person={emp} isTransition={true} />
                  ))
                )}
              </>
            )}

            {peopleTab === 'related' && (
              <>
                {(loadingRelated || profileStatus === 'loading') ? (
                  <div className="flex items-center justify-center py-24">
                    <Loader2 className="w-8 h-8 animate-spin" style={{ color: accentColor }} />
                  </div>
                ) : profileStatus === 'missing' ? (
                  <div style={{ padding: 24, textAlign: 'center' }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                      <UserIcon style={{ width: 22, height: 22, color: '#F59E0B' }} />
                    </div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.85)', marginBottom: 6 }}>Complete your profile</p>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 16, lineHeight: 1.5 }}>Add work or education details to see people from your past network.</p>
                    <button onClick={() => navigate('/profile-setup')} style={{ padding: '8px 20px', borderRadius: 99, background: 'rgba(245,158,11,0.1)', color: '#F59E0B', fontSize: 12, fontWeight: 700, border: '1px solid rgba(245,158,11,0.2)', cursor: 'pointer' }}>
                      Setup Profile
                    </button>
                  </div>
                ) : profileStatus === 'error' ? (
                  <div style={{ padding: 20, fontSize: 12, color: 'rgba(255,255,255,0.35)', textAlign: 'center' }}>Unable to load profile data.</div>
                ) : filteredRelatedBackground.length === 0 ? (
                  <div style={{ padding: 20, fontSize: 13, color: 'rgba(255,255,255,0.35)', lineHeight: 1.6 }}>
                    {String(roleFilter || '').trim()
                      ? 'No related people match this role filter.'
                      : (showOnlyMatched && hasConnectionFilters)
                        ? 'No related people match your advanced filters.'
                        : 'No people related to your background were found.'}
                  </div>
                ) : (
                  filteredRelatedBackground.map((person) => (
                    <PersonRow key={person.employee_id} person={person} isTransition={false} />
                  ))
                )}
              </>
            )}
          </div>

          {/* Right: detail panel */}
          <div className="cd-right-panel" style={{ flex: 1, overflow: 'auto', minWidth: 0, background: selectedPerson ? 'rgba(255,255,255,0.015)' : 'transparent' }}>
            <AnimatePresence mode="wait">
              {selectedPerson ? (
                <motion.div key={selectedPerson.employee_id} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }} transition={{ duration: 0.2 }} style={{ height: '100%', position: 'relative' }}>
                  {/* Close button */}
                  <div style={{ position: 'absolute', top: 8, right: 12, zIndex: 10 }}>
                    <button onClick={() => setSelectedPerson(null)} style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(255,255,255,0.5)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                    >
                      <X size={13} />
                    </button>
                  </div>
                  <DetailPanel
                    person={selectedPerson}
                    isTransition={peopleTab === 'transition'}
                    detailLoading={detailLoadingId === selectedPerson.employee_id}
                    detailError={detailError}
                  />
                </motion.div>
              ) : (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <DetailPanel person={null} isTransition={peopleTab === 'transition'} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      <style>{`
        .cd-two-panel {
          display: flex;
          height: calc(100vh - 1px);
          position: sticky;
          top: 0;
          overflow: hidden;
        }
        .cd-left-panel {
          width: 300px;
          flex-shrink: 0;
          border-right: 1px solid rgba(255,255,255,0.07);
          overflow-y: auto;
          display: flex;
          flex-direction: column;
        }
        .cd-row-chevron { display: none; }
        .cd-mobile-detail { display: none; border-bottom: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.02); }
        @media (max-width: 640px) {
          .cd-left-panel { width: 100%; }
          .cd-right-panel { display: none; }
          .cd-row-chevron { display: block; }
          .cd-mobile-detail { display: block; }
          .cd-two-panel { flex-direction: column; height: auto; position: static; }
        }
      `}</style>
    </div>
  );
};

export default CompanyDetails;
